import React, { useState, useEffect } from 'react';

interface FloatingWhatsAppProps {
  phoneNumber: string;
  message?: string;
  companyName?: string;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  offset?: number;
  hideOnMobile?: boolean;
  businessHours?: {
    start: number;
    end: number;
    timezone?: string;
  };
  className?: string;
}

const FloatingWhatsApp: React.FC<FloatingWhatsAppProps> = ({
  phoneNumber,
  message = 'Hola, me interesa obtener información sobre los servicios sanitarios portátiles.',
  companyName = 'Junisama Inversiones S.A.S',
  position = 'bottom-right',
  offset = 20,
  hideOnMobile = false,
  businessHours,
  className = '',
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isWithinBusinessHours, setIsWithinBusinessHours] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 100;
      setIsVisible(scrolled);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial state

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (businessHours) {
      const checkBusinessHours = () => {
        const now = new Date();
        const currentHour = now.getHours();
        const withinHours = currentHour >= businessHours.start && currentHour < businessHours.end;
        setIsWithinBusinessHours(withinHours);
      };

      checkBusinessHours();
      const interval = setInterval(checkBusinessHours, 60000); // Check every minute

      return () => clearInterval(interval);
    }
  }, [businessHours]);

  const formatPhoneNumber = (phone: string) => {
    return phone.replace(/\D/g, '').replace(/^57/, '');
  };

  const generateWhatsAppURL = () => {
    const formattedPhone = formatPhoneNumber(phoneNumber);
    const encodedMessage = encodeURIComponent(message);
    return `https://wa.me/57${formattedPhone}?text=${encodedMessage}`;
  };

  const handleClick = () => {
    window.open(generateWhatsAppURL(), '_blank', 'noopener,noreferrer');
  };

  const getPositionClasses = () => {
    const positions = {
      'bottom-right': 'whatsapp-bottom-right',
      'bottom-left': 'whatsapp-bottom-left',
      'top-right': 'whatsapp-top-right',
      'top-left': 'whatsapp-top-left',
    };
    return positions[position];
  };

  if (hideOnMobile && window.innerWidth < 768) {
    return null;
  }

  return (
    <div
      className={`floating-whatsapp ${getPositionClasses()} ${isVisible ? 'floating-whatsapp-visible' : ''} ${className}`}
      style={{ [position.includes('bottom') ? 'bottom' : 'top']: offset, [position.includes('right') ? 'right' : 'left']: offset }}
    >
      {/* Status Indicator - Always Online */}
      <div className="whatsapp-status status-online">
        <span className="status-text">En línea</span>
      </div>

      {/* Chat Bubble */}
      {isExpanded && (
        <div className="whatsapp-chat-bubble">
          <div className="chat-bubble-header">
            <div className="chat-bubble-avatar">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
              </svg>
            </div>
            <div className="chat-bubble-info">
              <div className="chat-bubble-name">{companyName}</div>
              <div className="chat-bubble-subtitle">Respuesta rápida</div>
            </div>
            <button
              className="chat-bubble-close"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(false);
              }}
              aria-label="Cerrar chat"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <div className="chat-bubble-body">
            <div className="chat-message">
              <div className="chat-message-text">
                ¡Hola! 👋<br />
                ¿Cómo podemos ayudarte con tus necesidades de servicios sanitarios portátiles?
              </div>
              <div className="chat-message-time">
                {new Date().toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>
          <div className="chat-bubble-footer">
            <button
              className="chat-bubble-reply-btn"
              onClick={handleClick}
              aria-label="Responder por WhatsApp"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="m22 2-7 20-4-9-9-4z"/>
                <path d="m22 2-10 10"/>
              </svg>
              Responder
            </button>
          </div>
        </div>
      )}

      {/* Main WhatsApp Button */}
      <button
        className="whatsapp-main-button"
        onClick={() => {
          if (isExpanded) {
            handleClick();
          } else {
            setIsExpanded(true);
          }
        }}
        onMouseEnter={() => {
          if (!isExpanded) {
            setIsExpanded(true);
          }
        }}
        onMouseLeave={() => {
          setIsExpanded(false);
        }}
        aria-label={`Contactar por WhatsApp a ${companyName}`}
      >
        <svg 
          className="whatsapp-icon" 
          width="28" 
          height="28" 
          viewBox="0 0 24 24" 
          fill="currentColor"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
        </svg>
        
        {/* Notification Badge */}
        <div className="whatsapp-notification-badge">
          <div className="notification-dot"></div>
        </div>
        
        {/* Pulse Animation */}
        <div className="whatsapp-pulse-ring"></div>
      </button>
    </div>
  );
};

export default FloatingWhatsApp;